import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  LinearProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import StopIcon from '@mui/icons-material/Stop';
import { Title, useNotify } from 'react-admin';
import { useNavigate, useParams } from 'react-router-dom';
import useAuthorize from '../hooks/useAuthorize';
import { WHATSAPP_MODULE_ROLES } from '../roles/portalRoles';
import { cancelJob, fetchJob, fetchRecipients } from './whatsappBroadcastApi';
import { BroadcastJob, BroadcastRecipient } from './types';

const WhatsAppBroadcastShow = () => {
  useAuthorize(WHATSAPP_MODULE_ROLES);
  const { id = '' } = useParams();
  const notify = useNotify();
  const navigate = useNavigate();
  const [job, setJob] = useState<BroadcastJob | null>(null);
  const [recipients, setRecipients] = useState<BroadcastRecipient[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [total, setTotal] = useState(0);
  const perPage = 50;

  const load = async () => {
    if (!id) return;
    setLoading(true);
    try {
      const [jobData, recipientData] = await Promise.all([
        fetchJob(id),
        fetchRecipients(id, page, perPage),
      ]);
      setJob(jobData);
      setRecipients(recipientData.recipients);
      setTotal(recipientData.total);
    } catch (error) {
      console.error(error);
      notify('Failed to load broadcast job', { type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [id, page]);

  const handleCancel = async () => {
    if (!id) return;
    try {
      const updated = await cancelJob(id);
      setJob(updated);
      notify('Job cancelled', { type: 'info' });
    } catch (error: any) {
      notify(error?.response?.data?.error || 'Failed to cancel job', { type: 'error' });
    }
  };

  const progress = job && job.total_recipients
    ? Math.min(100, Math.round((job.sent_count / job.total_recipients) * 100))
    : 0;

  return (
    <Box sx={{ p: 2 }}>
      <Title title="Broadcast Job" />
      <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
        <Button onClick={() => navigate('/whatsapp-broadcasts')}>Back to jobs</Button>
        <Button startIcon={<RefreshIcon />} onClick={load} disabled={loading}>Refresh</Button>
        {job && ['queued', 'running'].includes(job.status) && (
          <Button color="warning" startIcon={<StopIcon />} onClick={handleCancel}>Cancel job</Button>
        )}
      </Box>

      {job && (
        <Paper sx={{ p: 2, mb: 2 }}>
          <Typography variant="h6">{job.template_name}</Typography>
          <Box sx={{ display: 'flex', gap: 1, my: 1 }}>
            <Chip label={job.provider} size="small" />
            <Chip label={job.status} size="small" color="primary" />
            {job.dry_run && <Chip label="Dry run" size="small" color="warning" />}
          </Box>
          <Typography variant="body2">Created by: {job.created_by_user_name || job.created_by_user_id || '—'}</Typography>
          <Typography variant="body2">Started: {job.started_at ? new Date(job.started_at).toLocaleString() : '—'}</Typography>
          <Typography variant="body2">Completed: {job.completed_at ? new Date(job.completed_at).toLocaleString() : '—'}</Typography>
          <Typography variant="body1" sx={{ mt: 1 }}>
            Sent {job.sent_count} / {job.total_recipients} · Delivered {job.delivered_count} · Read {job.read_count} · Failed {job.failed_count}
          </Typography>
          <LinearProgress variant="determinate" value={progress} sx={{ mt: 1 }} />
          {job.error_message && (
            <Typography color="error" sx={{ mt: 1 }}>{job.error_message}</Typography>
          )}
        </Paper>
      )}

      <Paper>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Customer</TableCell>
              <TableCell>Phone</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Message ID</TableCell>
              <TableCell>Error</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {recipients.map((row) => (
              <TableRow key={row.id}>
                <TableCell>{row.customer_name || row.customer_id || '—'}</TableCell>
                <TableCell>{row.phone_e164}</TableCell>
                <TableCell>{row.status}</TableCell>
                <TableCell>{row.provider_message_id || '—'}</TableCell>
                <TableCell>{row.error_message || '—'}</TableCell>
              </TableRow>
            ))}
            {!loading && recipients.length === 0 && (
              <TableRow>
                <TableCell colSpan={5}>No recipients</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Paper>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
        <Button disabled={page === 0} onClick={() => setPage((p) => Math.max(0, p - 1))}>Previous</Button>
        <Typography variant="body2">Page {page + 1} · {total} recipients</Typography>
        <Button disabled={(page + 1) * perPage >= total} onClick={() => setPage((p) => p + 1)}>Next</Button>
      </Box>
    </Box>
  );
};

export default WhatsAppBroadcastShow;
